// components/AntifraudMethodTable.jsx
import React, { useMemo } from "react";
import GenericTable from "./GenericTable";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { IconButton, Tooltip, Box, Chip, Switch, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

const tipoProviderLabel = (tipo) => {
  const n = Number(tipo);
  if (n === 1) return "Konduto";
  if (n === 2) return "ClearSale";
  if (n === 3) return "FControl";
  return "Custom";
};

const ambienteLabel = (a, tf) =>
  a === "production"
    ? tf("antifraudMethodTable.environmentProduction")
    : tf("antifraudMethodTable.environmentSandbox");

const AntifraudMethodTable = ({
  methods = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onEdit,
  onDelete,
  onDeleteSelected,
  onToggleStatus,
  isLoading,
}) => {
  const { t } = useTranslation("settings");
  const rows = useMemo(
    () =>
      methods.map((m) => ({
        ...m,
        id: m.id,
        nome: m.nome || t("shared.notApplicable"),
        tipo_label: tipoProviderLabel(m.tipo),
        ambiente_label: ambienteLabel(m.ambiente, t),
        status_label: m.status === 1 ? t("shared.active") : t("shared.inactive"),
      })),
    [methods, t]
  );

  const columns = [
    {
      field: "nome",
      headerName: t("antifraudMethodTable.colAntifraud"),
      width: 380,
      renderCell: (p) => (
        <Stack spacing={0.3}>
          <Typography fontWeight={600} noWrap>{p.row.nome}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {p.row.tipo_label} • {p.row.ambiente_label}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "status",
      headerName: t("shared.status"),
      width: 160,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Switch size="small" checked={Number(params.row.status) === 1} onChange={() => onToggleStatus?.(params.row)} />
          <Chip size="small" label={params.row.status_label} color={Number(params.row.status) === 1 ? "success" : "default"} variant="outlined" />
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: t("shared.actions"),
      width: 140,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Tooltip title={t("shared.edit")}>
            <IconButton size="small" onClick={() => onEdit?.(params.row)}><EditRoundedIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={t("shared.delete")}>
            <IconButton size="small" onClick={() => onDelete?.(params.row.id)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      onExportPDFTitle={t("antifraudMethodTable.exportTitle")}
      exportFileName="metodos_antifraude"
      onDeleteSelected={onDeleteSelected}
      searchPlaceholder={t("antifraudMethodTable.searchPlaceholder")}
      pageSizeOptions={[10, 25, 50, 100]}
      dense
    />
  );
};

export default AntifraudMethodTable;
